import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';

import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import { RadioButtonIcon } from '../icons/RadioButtonIcon';
import { FormDropdownClearButton } from '../CommonFormComponents';

/**
 * ComboBox コンポーネント
 *
 * テキスト入力をベースとしたドロップダウンリストコンポーネント
 * 選択肢以外のテキストを入力することができる
 *
 * @param {Object} props - ComboBox コンポーネントのプロパティ
 * @param {boolean} props.disabled - ドロップダウンが無効化されている場合はtrue
 * @param {string} props.value - ドロップダウンの現在の値
 * @param {string} props.listId - リストのユニークID
 * @param {Array} props.comboBoxList - ドロップダウンに表示される項目の配列
 * @param {string} props.comboBoxValueKey - 項目の値を表示するために使用するキー
 * @param {Function} props.onChange - 項目が選択された時のコールバック関数
 * @param {number} props.maxLength - 入力の最大長
 */
const ComboBox = ({
  disabled = false,
  value = '',
  listId = '',
  comboBoxList = [],
  comboBoxValueKey = 'id',
  onChange,
  maxLength,
}) => {
  const [opened, setOpened] = useState(false);
  const [filteredList, setFilteredList] = useState(comboBoxList);
  const [isOnHover, setIsOnHover] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    setFilteredList(filterListBySearch(comboBoxList, value));

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [comboBoxList]);

  const handleOnChange = value => {
    if (_.isFunction(onChange)) {
      const event = {
        target: { value },
      };
      setFilteredList(filterListBySearch(comboBoxList, value));
      onChange(event);
    }
  };

  // プルダウンから選択せずに入力した場合、プルダウン内容を入力したstringで絞り込む
  const filterListBySearch = (list, search) =>
    _.filter(list, item =>
      _.includes(item[comboBoxValueKey], _.defaultTo(search, ''))
    );

  const handleSelectedRow = option => {
    handleOnChange(option[comboBoxValueKey]);
    setOpened(false);
  };

  return (
    <div className='form-dropdown dropdown-multi-select ComboBox'>
      {disabled ? (
        <span>{value}</span>
      ) : (
        <div ref={dropdownRef}>
          <div
            className={`dropdown-multi-select__header ${opened ? 'show' : ''}`}
          >
            <input
              type='text'
              className='dropdown-select'
              onMouseEnter={() => setIsOnHover(true)}
              onMouseLeave={() => setIsOnHover(false)}
              onClick={() => setOpened(true)}
              onChange={event => handleOnChange(event.target.value)}
              value={_.defaultTo(value, '')}
              maxLength={maxLength}
              list={listId}
            />
          </div>
          {(isOnHover || opened) && (
            <ArrowDownIcon className='icon--2_4x form-list__arrow-icon' />
          )}
          {opened && (
            <div className='dropdown dropdown--dropdown-list dropdown--ComboBox show'>
              <div className='dropdown__item dropdown__item--hidden'>
                  <div className='dropdown-single-select__item'>
                    <FormDropdownClearButton
                      onClick={() => handleOnChange('')}
                    />
                  </div>
                  <div className='dropdown-single-select__scroll'>
                    <ul className='single-select'>
                      {_.map(filteredList, (option, index) => (
                        <li key={index}>
                          <label
                            className='radio'
                            htmlFor={`${listId}-${index}`}
                          >
                            <input
                              id={`${listId}-${index}`}
                              type='radio'
                              value={option[comboBoxValueKey]}
                              checked={value === option[comboBoxValueKey]}
                              onChange={() => handleSelectedRow(option)}
                            />
                            <RadioButtonIcon className='icon--1_6x' />
                            <span className='single-select__label-text'>
                              {option[comboBoxValueKey]}
                            </span>
                          </label>
                        </li>
                      ))}
                      {filteredList.length === 0 && (
                        <li className='radio'>
                          <span className='single-select__label-text'>
                            {value}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComboBox;



// WEBPACK FOOTER //
// ./src/components/combobox/ComboBox.jsx